import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Stack, Text, Columns, Column, Inline } from "braid-design-system";
import { useRef, useState, useEffect } from "react";
import { ResponsiveContainer, BarChart, CartesianGrid, Tooltip, Legend, Bar, XAxis, YAxis } from "recharts";
import { StackBarChartTick } from "./StackBarChartTick.mjs";
import { barChart } from "./StackBarChart.css.mjs";
const StackBarChartTooltip = ({
  tooltipProps: { active, payload, label },
  noDataTooltip,
  colorMap,
  labelMap,
  locale,
  isNVLEnabled
}) => {
  var _a, _b;
  const numberFormatter = new Intl.NumberFormat(locale);
  const maximumRef = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    setWidth(maximumRef.current ? maximumRef.current.offsetWidth : 0);
  }, [(_a = maximumRef == null ? void 0 : maximumRef.current) == null ? void 0 : _a.offsetWidth]);
  const tooltipTotal = (payload == null ? void 0 : payload.reduce(
    (total, currentValue) => total + (currentValue.value ?? 0),
    0
  )) ?? 0;
  if (!active) return null;
  const hasAdTypeCount = (_b = payload == null ? void 0 : payload.reduce(
    (maxValue, currentValue) => (maxValue.value ?? 0) > (currentValue.value ?? 0) ? maxValue : currentValue,
    { value: 0 }
  )) == null ? void 0 : _b.value;
  if (!hasAdTypeCount) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        boxShadow: isNVLEnabled ? "borderNeutralLight" : void 0,
        background: "body",
        borderRadius: isNVLEnabled ? "standard" : void 0,
        padding: "small",
        children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", children: label }),
          /* @__PURE__ */ jsx(Text, { size: "small", children: noDataTooltip })
        ] })
      }
    );
  }
  return /* @__PURE__ */ jsx(
    Box,
    {
      boxShadow: isNVLEnabled ? "borderNeutralLight" : void 0,
      borderRadius: isNVLEnabled ? "standard" : void 0,
      background: "body",
      padding: "small",
      maxWidth: "xsmall",
      children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: "center", children: [
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", maxLines: 1, children: label }) }),
          /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { textAlign: "right", children: /* @__PURE__ */ jsx(Box, { display: "inlineBlock", ref: maximumRef, children: /* @__PURE__ */ jsx(Text, { weight: "strong", size: "small", align: "right", children: numberFormatter.format(tooltipTotal) }) }) }) })
        ] }),
        /* @__PURE__ */ jsx(Stack, { space: "xsmall", children: payload.map((p) => /* @__PURE__ */ jsxs(Columns, { space: "small", alignY: "center", children: [
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Columns, { space: "xsmall", children: [
            /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
              Box,
              {
                style: {
                  width: isNVLEnabled ? "10px" : "12px",
                  // The width is set to 10px so that it can form a circle instead of oval when borderRadius is set to full
                  height: "10px",
                  backgroundColor: colorMap[p.name]
                },
                borderRadius: isNVLEnabled ? "full" : "none"
              }
            ) }),
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Text, { size: "xsmall", maxLines: 1, children: (labelMap == null ? void 0 : labelMap[p.name]) || p.name }) })
          ] }) }),
          /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { style: { width: `${width}px` }, children: /* @__PURE__ */ jsx(Text, { size: "xsmall", align: "right", children: numberFormatter.format(p.value) }) }) })
        ] }, p.name)) })
      ] })
    }
  );
};
const StackBarChartLegend = ({
  legendProps: { payload },
  labelMap,
  isNVLEnabled
}) => /* @__PURE__ */ jsx(
  Box,
  {
    paddingTop: isNVLEnabled ? "none" : "large",
    paddingBottom: isNVLEnabled ? "medium" : "none",
    children: /* @__PURE__ */ jsx(Inline, { space: "gutter", children: payload == null ? void 0 : payload.map((p, index) => /* @__PURE__ */ jsxs(Box, { display: "flex", flexWrap: "nowrap", children: [
      /* @__PURE__ */ jsx(
        Box,
        {
          style: {
            width: isNVLEnabled ? "10px" : "12px",
            // The width is set to 10px so that it can form a circle instead of oval when borderRadius is set to full
            height: "10px",
            backgroundColor: p.color
          },
          marginRight: "xsmall",
          borderRadius: isNVLEnabled ? "full" : "none"
        }
      ),
      /* @__PURE__ */ jsx(Text, { size: isNVLEnabled ? "small" : "standard", children: (labelMap == null ? void 0 : labelMap[p.value]) || p.value })
    ] }, `item-${index}`)) })
  }
);
const StackBarChart = ({
  data,
  totalDataCount,
  locale,
  colorMap,
  labelMap,
  noDataTooltip,
  height = 410,
  isNVLEnabled,
  xTicks
}) => {
  const [yAxisWidth, setYAxisWidth] = useState(0);
  const onTickRender = (width) => {
    if (width > yAxisWidth) {
      setYAxisWidth(width);
    }
  };
  const sumForItem = (item) => {
    const { name, ...restOfItem } = item;
    return Object.entries(restOfItem).filter(([key]) => key !== "name").reduce((total, [_key, value]) => total + (value ?? 0), 0);
  };
  const calculateTicksForYAxis = (items) => {
    const max = Math.max(...items.map((item) => sumForItem(item)));
    const ticks = [];
    const noOfTicks = 4;
    const tickIntervals = max / noOfTicks;
    ticks.push(0);
    for (let i = 1; i < noOfTicks; i++) {
      ticks.push(Math.ceil(tickIntervals * i));
    }
    ticks.push(max);
    return ticks;
  };
  return /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsx(Box, { style: { height: `${height}px` }, children: /* @__PURE__ */ jsx(ResponsiveContainer, { width: "100%", height: "100%", children: /* @__PURE__ */ jsxs(
    BarChart,
    {
      data,
      className: data.length > 90 ? "" : barChart,
      children: [
        /* @__PURE__ */ jsx(CartesianGrid, { strokeDasharray: "3 3", vertical: false }),
        /* @__PURE__ */ jsx(
          Tooltip,
          {
            cursor: { fill: "#EAECF1" },
            content: (props) => /* @__PURE__ */ jsx(
              StackBarChartTooltip,
              {
                tooltipProps: props,
                colorMap,
                labelMap,
                noDataTooltip,
                locale,
                isNVLEnabled
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Legend,
          {
            content: (props) => /* @__PURE__ */ jsx(
              StackBarChartLegend,
              {
                legendProps: props,
                labelMap,
                isNVLEnabled
              }
            ),
            align: "left",
            verticalAlign: isNVLEnabled ? "top" : "bottom"
          }
        ),
        totalDataCount.map(({ key }) => /* @__PURE__ */ jsx(
          Bar,
          {
            name: key,
            dataKey: key,
            stackId: "a",
            fill: colorMap[key]
          },
          key
        )),
        /* @__PURE__ */ jsx(
          XAxis,
          {
            dataKey: "name",
            minTickGap: 15,
            tick: (props) => /* @__PURE__ */ jsx(
              StackBarChartTick,
              {
                axisProps: props,
                locale,
                axis: "x",
                isNVLEnabled
              }
            ),
            interval: "preserveStart",
            ticks: xTicks
          }
        ),
        /* @__PURE__ */ jsx(
          YAxis,
          {
            width: yAxisWidth > 0 ? yAxisWidth + 5 : void 0,
            ticks: calculateTicksForYAxis(data),
            tick: (props) => /* @__PURE__ */ jsx(
              StackBarChartTick,
              {
                axisProps: props,
                locale,
                onTickRender,
                axis: "y",
                isNVLEnabled
              }
            ),
            domain: [0, "dataMax"],
            allowDecimals: false
          }
        )
      ]
    }
  ) }) }) });
};
export {
  StackBarChart
};
